/**
 * Custom hook for Speed Compliance Report
 * Handles state and API calls for speed compliance data
 */

import { useState, useCallback } from 'react';
import api from '@/services/api/client';
import type {
  SpeedComplianceStats,
  VehicleSpeedStats,
  Summary,
  TrendData,
  SpeedSegment,
  TabType,
} from '@/types/speedCompliance';
import { getViolationRate } from '@/types/speedCompliance';

export const useSpeedCompliance = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  
  const [summary, setSummary] = useState<Summary | null>(null);
  const [orgStats, setOrgStats] = useState<SpeedComplianceStats[]>([]);
  const [vehicleStats, setVehicleStats] = useState<VehicleSpeedStats[]>([]);
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [speedSegments, setSpeedSegments] = useState<SpeedSegment[]>([]);
  const [segmentsSummary, setSegmentsSummary] = useState<{
    totalPoints: number;
    overspeedPoints: number;
    violationPct: number;
  } | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiMessage, setApiMessage] = useState<string | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!selectedOrg) return;
    
    setDataLoaded(true);
    setLoading(true);
    setApiMessage(null);
    
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        organizationId: selectedOrg,
      });

      const [reportRes, vehiclesRes, trendRes, segmentsRes] = await Promise.all([
        api.get(`/api/reports/speed-compliance?${params}`),
        api.get(`/api/reports/speed-compliance/vehicles?${params}`),
        api.get(`/api/reports/speed-compliance/trend?${params}&granularity=day`),
        api.get(`/api/reports/speed-compliance/segments?${params}`),
      ]);

      if (reportRes.data.success) {
        setSummary(reportRes.data.data.summary);
        setOrgStats(reportRes.data.data.organizations);
      }
      if (vehiclesRes.data.success) {
        setVehicleStats(vehiclesRes.data.data);
      }
      if (trendRes.data.success) {
        setTrendData(trendRes.data.data);
      }
      if (segmentsRes.data.success) {
        setSpeedSegments(segmentsRes.data.data.segments || []);
        setSegmentsSummary(segmentsRes.data.data.summary ?? null);
      }
      
      const msg = (reportRes.data as { message?: string }).message
        ?? (vehiclesRes.data as { message?: string }).message
        ?? (trendRes.data as { message?: string }).message
        ?? null;
      setApiMessage(msg);
    } catch (error) {
      console.error('[SpeedCompliance] Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, dateRange]);

  // Export to Excel
  const exportToExcel = useCallback(() => {
    const headers = ['Organisation/Fordon', 'Resor', 'Distans (km)', 'Överträdelser', 'Överträdelse (%)', 'Medelhastighet', 'Max hastighet'];
    
    const rows = activeTab === 'vehicles' 
      ? vehicleStats.map(v => [
          v.registrationNumber,
          v.totalTrips,
          v.totalDistance,
          v.totalOverspeeds,
          getViolationRate(v.complianceRate),
          v.avgSpeed,
          v.maxSpeed,
        ])
      : orgStats.map(o => [
          o.organizationName,
          o.totalTrips,
          o.totalDistance,
          o.totalOverspeeds,
          getViolationRate(o.complianceRate),
          o.avgSpeed,
          o.maxSpeed,
        ]);

    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hastighetsoverträdelser_${dateRange.startDate}_${dateRange.endDate}.csv`;
    link.click();
  }, [activeTab, vehicleStats, orgStats, dateRange]);

  return {
    // State
    loading,
    activeTab,
    setActiveTab,
    selectedOrg,
    setSelectedOrg,
    dateRange,
    setDateRange,
    summary,
    orgStats,
    vehicleStats,
    trendData,
    speedSegments,
    segmentsSummary,
    dataLoaded,
    apiMessage,
    
    // Actions
    fetchData,
    exportToExcel,
  };
};
